/**
 * /clarity-card — one page of the Passport, made shareable.
 *
 * Built from the SAME dossier model the Passport renders (`useDossier`), so
 * the card and the report can never disagree. Only the cover band and the
 * identity page are on it, and no score, percentage, bar, pay band or ranked
 * job title is ever printed — that is the whole point of the asset.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, MessageCircle, Share2 } from "lucide-react";
import { AssessmentV2Provider, useAssessmentV2 } from "@/features/assessment-v2/store";
import { parseBand } from "@/features/assessment-v2/band";
import { useDossier } from "@/features/assessment-v2/useDossier";
import { useCanonicalTakeover } from "@/features/assessment-v2/useCanonicalReport";
import ClarityCard, {
  CLARITY_THEMES, type ClarityCardTheme,
} from "@/features/assessment-v2/components/ClarityCard";
import { REPORT_NAME } from "@/features/assessment-v2/dossier";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { fetchMyReferralAssets } from "@/lib/referral";
import { cloudStorage } from "@/lib/cloudStore";

/** The theme the learner last picked, kept on their record, not this device. */
const THEME_KEY = "wcc360.v2.clarity_card_theme";

function readTheme(): ClarityCardTheme {
  const raw = cloudStorage.getItem(THEME_KEY);
  return raw === "dark" || raw === "light" || raw === "glass" ? raw : "glass";
}

function fileName(name: string): string {
  const slug = name.normalize("NFKD").replace(/[^\w\s-]/g, "").trim()
    .replace(/\s+/g, "-").slice(0, 40) || "Card";
  return `WeKIT-Clarity-Card-${slug}.png`;
}

function ClarityCardInner() {
  const { state, band } = useAssessmentV2();
  const takeover = useCanonicalTakeover(band);
  const { dossier, error } = useDossier(state, band, {
    reportHash: takeover.canonical?.reportHash ?? null,
    attemptId: takeover.sessionId,
  });
  const { user } = useAuth();
  const cardRef = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useState<ClarityCardTheme>(readTheme);
  const [handle, setHandle] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    fetchMyReferralAssets(user.id).then((a) => setHandle(a?.handle ?? null)).catch(() => {});
  }, [user?.id]);

  const pick = (next: ClarityCardTheme) => {
    setTheme(next);
    cloudStorage.setItem(THEME_KEY, next);
  };

  const shareUrl = useMemo(
    () => (handle ? `https://wekit.ai/ref/${handle}` : "https://wekit.ai"),
    [handle],
  );
  const shareText = dossier
    ? `${dossier.identity.pairLabel}. That's what my WeKIT™ Career Clarity 360 Passport says — find yours:`
    : "Find your direction with WeKIT™ Career Clarity 360";

  /**
   * The card is the payload. Every share path renders the PNG first — a
   * link-only share arrives as a bare URL with no preview, which is exactly
   * what the learner did not mean by "share my card".
   */
  const renderPng = async (): Promise<{ dataUrl: string; file: File }> => {
    const { toPng } = await import("html-to-image");
    const dataUrl = await toPng(cardRef.current!, { pixelRatio: 2, cacheBust: true });
    const blob = await (await fetch(dataUrl)).blob();
    const name = fileName(dossier!.learnerName);
    return { dataUrl, file: new File([blob], name, { type: "image/png" }) };
  };

  const saveToDisk = (dataUrl: string, name: string) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = name;
    a.click();
  };

  const download = async () => {
    if (!cardRef.current || !dossier) return;
    setExporting(true);
    try {
      const { dataUrl, file } = await renderPng();
      saveToDisk(dataUrl, file.name);
      toast({ title: "Saved", description: "It's in your downloads — post it wherever you like." });
    } catch (e) {
      toast({
        title: "Couldn't make the image",
        description: e instanceof Error ? e.message : "Try another theme.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  /**
   * Share the image itself. Web Share Level 2 (`files`) carries the PNG into
   * WhatsApp, Instagram, Messages and the rest. Where files aren't supported
   * we copy the image to the clipboard, and if even that is refused we save it
   * so the learner still has something to attach — the text and referral link
   * ride along in every case.
   */
  const nativeShare = async () => {
    if (!cardRef.current || !dossier) return;
    setExporting(true);
    try {
      const { dataUrl, file } = await renderPng();

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Clarity Card", text: `${shareText} ${shareUrl}` });
        return;
      }

      try {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": file }),
        ]);
        toast({ title: "Card copied", description: "Paste the image straight into your post or chat." });
        return;
      } catch { /* clipboard images are blocked in some browsers */ }

      saveToDisk(dataUrl, file.name);
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`).catch(() => {});
      toast({
        title: "Card saved",
        description: "Your browser can't attach images directly — the PNG is in your downloads and the caption is copied.",
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return; // learner cancelled
      toast({
        title: "Couldn't share the card",
        description: e instanceof Error ? e.message : "Download the PNG instead.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  /**
   * WhatsApp's web link can only carry text, so the image goes to the share
   * sheet when the device has one, and otherwise gets saved to attach.
   */
  const shareWhatsApp = async () => {
    if (!cardRef.current || !dossier) return;
    setExporting(true);
    try {
      const { dataUrl, file } = await renderPng();
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: `${shareText} ${shareUrl}` });
        return;
      }
      saveToDisk(dataUrl, file.name);
      window.open(
        `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
        "_blank", "noopener,noreferrer",
      );
      toast({ title: "Card saved", description: "Attach it to the WhatsApp message we just opened." });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      toast({ title: "Couldn't share the card", description: "Download the PNG instead.", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };


  if (error) {
    return (
      <div className="min-h-dvh grid place-items-center bg-background px-6 text-center">
        <div className="max-w-md space-y-4">
          <h1 className="font-display text-2xl">Your card didn't load. Nothing you said is lost.</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Link to="/assessment/v2/result" className="inline-flex min-h-[48px] items-center rounded-2xl bg-primary px-6 text-primary-foreground">
            Back to your results
          </Link>
        </div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="min-h-dvh grid place-items-center bg-background px-6">
        <p className="animate-pulse text-sm text-muted-foreground">Making your card…</p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4">
          <Link
            to="/assessment/v2/dossier"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg text-sm text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Your {REPORT_NAME}
          </Link>
          <span className="text-xs uppercase tracking-[0.2em] text-primary">Clarity Card</span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:py-12">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-medium sm:text-4xl">
            One page of your Passport, made to be shared.
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            The cover and who you are — nothing else. No scores, no pay, no ranked job
            titles, so it's safe to post anywhere.
          </p>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <div
              className="relative mx-auto w-full overflow-hidden rounded-3xl ring-1 ring-border"
              style={{ maxWidth: 480, aspectRatio: "1080 / 1350" }}
            >
              <div
                className="origin-top-left"
                style={{ width: 1080, height: 1350, transform: "scale(var(--clarity-scale, 0.44))" }}
                ref={(el) => {
                  if (!el) return;
                  const parent = el.parentElement!;
                  const update = () =>
                    el.style.setProperty("--clarity-scale", String(parent.clientWidth / 1080));
                  update();
                  new ResizeObserver(update).observe(parent);
                }}
              >
                <ClarityCard ref={cardRef} dossier={dossier} theme={theme} handle={handle} />
              </div>
            </div>
          </div>

          <div className="space-y-5 lg:col-span-2">
            <section className="rounded-3xl border border-border bg-card p-5">
              <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Theme
              </h2>
              <div className="mt-3 grid gap-2">
                {CLARITY_THEMES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    aria-pressed={theme === t.key}
                    onClick={() => pick(t.key)}
                    className={`flex min-h-[56px] items-center gap-3 rounded-2xl border p-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      theme === t.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"
                    }`}
                  >
                    <span
                      className="h-9 w-9 shrink-0 rounded-xl border border-border"
                      style={{ background: t.swatch }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{t.label}</span>
                      <span className="block text-xs text-muted-foreground">{t.hint}</span>
                    </span>
                  </button>
                ))}
              </div>
              <Button onClick={download} disabled={exporting} className="mt-4 h-12 w-full gap-2 rounded-2xl">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download PNG
              </Button>
            </section>

            <section className="rounded-3xl border border-border bg-card p-5">
              <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Share
              </h2>
              <div className="mt-3 grid gap-2">
                <Button
                  onClick={shareWhatsApp}
                  disabled={exporting}
                  variant="outline"
                  className="h-12 gap-2 rounded-2xl"
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                  WhatsApp
                </Button>
                <Button onClick={nativeShare} disabled={exporting} variant="outline" className="h-12 gap-2 rounded-2xl">
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                  Share the card
                </Button>
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                Sharing sends the card image itself, with your link in the caption. If your
                browser can't attach images, we save the PNG so you can add it yourself.
              </p>

            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function ClarityCardPage() {
  const [params] = useSearchParams();
  const band = parseBand(params.get("band"));
  return (
    <AssessmentV2Provider key={band} band={band}>
      <ClarityCardInner />
    </AssessmentV2Provider>
  );
}
